import React from "react";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import { Card, GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { MdDelete } from "react-icons/md";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setFITMiscellaneousDeleteIndex } from "./FITBookingSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import IconButton from "../../components/common/IconButton";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { ICurrency } from "../currency/currencyModel";

const FITBookingMiscellaneousArray = (
  fieldArrayRenderProps: FieldArrayRenderProps
) => {
  const dispatch = useAppDispatch();
  const FITMiscellaneousDeleteIndex = useAppSelector(
    (state) => state.FITBooking.FITMiscellaneousDeleteIndex
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const CurrencyList = useAppSelector((state) => state.currency.CurrencyList);

  const handleOpenDeleteDialog = (index: number) => {
    dispatch(openDialog("fitMiscellaneousDeleteDialog"));
    dispatch(setFITMiscellaneousDeleteIndex(index));
  };

  const handleDeleteAction = React.useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      dispatch(setFITMiscellaneousDeleteIndex(-1));
      if (
        !fieldArrayRenderProps.value ||
        fieldArrayRenderProps?.value?.length <= 1
      ) {
        fieldArrayRenderProps?.formRenderProps.onChange(
          "MiscellaneousCurrencyID",
          {
            value: 1,
          }
        );
        fieldArrayRenderProps?.formRenderProps.onChange(
          "MiscellaneousConversionRate",
          {
            value: 1,
          }
        );
      }
    },
    [fieldArrayRenderProps]
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        Title: "",
        TotalAmount: null,
      },
    });
  }, [fieldArrayRenderProps]);

  return (
    <GridLayout cols={[{ width: "100%" }]}>
      {/* {!fieldArrayRenderProps.value || fieldArrayRenderProps?.value?.length < 1
        ? pushElementInarray()
        : null} */}

      <GridLayoutItem
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Field
            id={"MiscellaneousCurrencyID"}
            name={"MiscellaneousCurrencyID"}
            label={"Currency"}
            placeholder="Currency"
            component={FormSelectionField}
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            onChange={(e) => {
              if (e?.value) {
                const conversionRate =
                  CurrencyList.find(
                    (currency: ICurrency) => currency.ID === e?.value
                  )?.CurrencyConversion || "1";

                fieldArrayRenderProps?.formRenderProps.onChange(
                  "MiscellaneousConversionRate",
                  {
                    value: +conversionRate,
                  }
                );
              } else {
                fieldArrayRenderProps?.formRenderProps.onChange(
                  "MiscellaneousConversionRate",
                  {
                    value: 1,
                  }
                );
              }
            }}
            options={CurrencyList?.map((currency: ICurrency) => {
              return {
                value: currency.ID,
                label: currency.CurrencyName,
              };
            })}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="MiscellaneousConversionRate"
            label="Conversion Rate"
            placeholder="0"
            component={FormNumericTextField}
            disabled={
              !fieldArrayRenderProps.value ||
              fieldArrayRenderProps?.value?.length < 1
            }
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            min={0}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="MiscellaneousMarkup"
            label="Miscellaneous Markup"
            placeholder="0"
            component={FormNumericTextField}
            min={0}
          />
        </div>
        {(!fieldArrayRenderProps.value ||
          fieldArrayRenderProps?.value?.length < 1) && (
          <div>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
              style={{ marginTop: 35 }}
            >
              Add Miscellaneous
            </Button>
          </div>
        )}
      </GridLayoutItem>
      {dialogName === "fitMiscellaneousDeleteDialog" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Miscellaneous"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() =>
                    handleDeleteAction(FITMiscellaneousDeleteIndex)
                  }
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value.map((Miscellaneous: any, index: number) => (
          <GridLayoutItem key={index}>
            <Card className="fieldarray-card">
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "30%" },
                  { width: "30%" },
                  { width: "30%" },
                  { width: "10%" },
                ]}
              >
                <GridLayoutItem>
                  <Field
                    name={`Miscellaneous.${index}.Title`}
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={
                      fieldArrayRenderProps?.value[index]?.TotalAmount
                        ? requiredValidator
                        : skipValidator
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`Miscellaneous.${index}.TotalAmount`}
                    label="Total Amount"
                    placeholder="0"
                    min={0}
                    component={FormNumericTextField}
                    validator={
                      fieldArrayRenderProps?.value[index]?.Title
                        ? requiredValidator
                        : skipValidator
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem className="text-end" colSpan={2}>
                  <IconButton
                    onClick={() => handleOpenDeleteDialog(index)}
                    // disabled={fieldArrayRenderProps.value.length === 1}
                    themeColor={"error"}
                    size={"small"}
                    fillMode={"solid"}
                    type="button"
                    style={{
                      height: 30,
                      width: 30,
                      marginTop: 35,
                    }}
                    icon={MdDelete}
                    iconClassName="absolute-position"
                    iconStyle={{ fontSize: "16px" }}
                  />
                </GridLayoutItem>
              </GridLayout>
            </Card>
          </GridLayoutItem>
        ))}

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps?.value?.length > 0 && (
          <GridLayoutItem style={{ textAlign: "end" }}>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Miscellaneous
            </Button>
          </GridLayoutItem>
        )}
    </GridLayout>
  );
};

export default FITBookingMiscellaneousArray;
